import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, MapPin, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Input } from './ui/input';
import { addressAutocompleteAPI } from '../api';
import { cn } from '../lib/utils';
import logger from '../utils/logger';

/**
 * Address Autocomplete Component
 * Provides real-time address suggestions as user types.
 * Uses ArcGIS suggest API with Montreal-focused results.
 */
// A civic number then a street: the typed text is worth searching as is (spec 118).
const TYPED_ADDRESS = /^\s*(\d+)\s+\S/;

const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  onSubmitText,
  placeholder = "Ex: 1234 Rue Saint-Denis, Montréal",
  className,
  disabled = false,
  'data-testid': testId = "address-autocomplete"
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState(null);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch suggestions with debounce
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await addressAutocompleteAPI.suggest(query, 6);
      setSuggestions(response.data.suggestions || []);
      setShowDropdown(true);
      setSelectedIndex(-1);
    } catch (err) {
      logger.error('Suggestion error:', err);
      setError('Erreur lors de la recherche');
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced input handler
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the API call
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelect = async (suggestion) => {
    const selectedAddress = suggestion.text;
    onChange(selectedAddress);
    setShowDropdown(false);
    setSuggestions([]);
    setSelectedIndex(-1);

    // Always call onSelect with the selected text at minimum
    if (!onSelect) return;

    // Try to validate and get full address details
    if (suggestion.magic_key) {
      try {
        const response = await addressAutocompleteAPI.validate(suggestion.magic_key);
        if (response.data.valid && response.data.address) {
          onSelect({
            ...response.data.address,
            selectedText: selectedAddress
          });
          return;
        }
      } catch (err) {
        logger.error('Validation error:', err);
      }
      
      // Fallback to direct geocode
      try {
        const geocodeResponse = await addressAutocompleteAPI.geocode(selectedAddress);
        if (geocodeResponse.data.found && geocodeResponse.data.result) {
          onSelect({
            ...geocodeResponse.data.result,
            selectedText: selectedAddress
          });
          return;
        }
      } catch (err) {
        logger.error('Geocode error:', err);
      }
    }
    
    // Last resort: call with just the selected text
    onSelect({ selectedText: selectedAddress, address: selectedAddress });
  };

  // The suggestions may not know the civic number; the typed text is then searched as is.
  const submitTyped = () => {
    setShowDropdown(false);
    setSelectedIndex(-1);
    if (onSubmitText) onSubmitText(value);
  };

  const typedNumber = (value.match(TYPED_ADDRESS) || [])[1];
  const offerTyped = Boolean(onSubmitText && typedNumber) &&
    !suggestions.some((s) => (s.text || '').trim().startsWith(`${typedNumber} `));

  // Keyboard navigation
  const handleKeyDown = (e) => {
    const highlighted = showDropdown && selectedIndex >= 0 && selectedIndex < suggestions.length;
    if (e.key === 'Enter' && !highlighted) {
      e.preventDefault();
      submitTyped();
      return;
    }
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10 pr-10"
          data-testid={testId}
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : value && value.length >= 3 ? (
            <Search className="w-4 h-4 text-muted-foreground" />
          ) : null}
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showDropdown && (suggestions.length > 0 || offerTyped) && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-64 overflow-auto"
          data-testid="address-suggestions"
        >
          {offerTyped && (
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2 border-b"
              onClick={submitTyped}
              data-testid="address-search-typed"
            >
              <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate">Rechercher « {value.trim()} »</span>
            </button>
          )}
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.magic_key || index}
              type="button"
              className={cn(
                "w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2",
                selectedIndex === index && "bg-muted"
              )}
              onClick={() => handleSelect(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{suggestion.text}</span>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showDropdown && !isLoading && value.length >= 3 && suggestions.length === 0 && !offerTyped && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg p-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="w-4 h-4" />
            <span>Aucune adresse trouvée. Vérifiez l&apos;orthographe.</span>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mt-1 text-xs text-destructive">{error}</div>
      )}

      {/* Help text */}
      {value.length > 0 && value.length < 3 && (
        <div className="mt-1 text-xs text-muted-foreground">
          Entrez au moins 3 caractères pour voir les suggestions
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
